// Shared enums / options for the HR & Payroll module.
// Values here are the exact strings the API sends and receives (see docs/HR_Payroll_Module.md).

export const HR_SCREENS = {
    dashboard: "HR Dashboard",
    employees: "HR Employees",
    attendance: "HR Attendance",
    leave: "HR Leave",
    salary: "HR Salary Structure",
    loans: "HR Loans",
    incentives: "HR Incentives",
    payroll: "HR Payroll Run",
    payslips: "HR Payslips",
    settlement: "HR Final Settlement",
    reports: "HR Reports",
    setup: "HR Setup",
};

export const toOptions = (list) => list.map((x) => ({ value: x, label: x }));
export const findOpt = (options, value) => options.find((o) => o.value === value) || null;

export const EMPLOYEE_STATUS = ["Active", "Probation", "On Notice", "Resigned", "Terminated", "Retired"];
export const EXIT_STATUSES = ["Resigned", "Terminated", "Retired"];
export const EMPLOYMENT_TYPES = ["Permanent", "Contract", "Probation", "Daily Wages", "Intern"];
export const GENDERS = ["Male", "Female"];
export const MARITAL_STATUS = ["Single", "Married", "Divorced", "Widowed"];
export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
export const PAYMENT_MODES = ["Bank Transfer", "Cash", "Cheque"];

// Attendance codes: P Present, LT Late, HD Half day (unpaid half), SL Short leave, A Absent, L Leave (paid),
// HL Half-day leave (paid), UL Unpaid leave, H Holiday, W Weekly off. L and HL come only from approved leave requests.
export const ATTENDANCE_STATUS = [
    { value: "P", label: "Present", color: "#16a34a" },
    { value: "LT", label: "Late", color: "#d97706" },
    { value: "HD", label: "Half Day", color: "#0891b2" },
    { value: "SL", label: "Short Leave", color: "#faad14" },
    { value: "A", label: "Absent", color: "#dc2626" },
    { value: "L", label: "Leave", color: "#7c3aed", leaveOnly: true },
    { value: "HL", label: "Half-day Leave", color: "#a78bfa", leaveOnly: true },
    { value: "UL", label: "Unpaid Leave", color: "#be185d" },
    { value: "H", label: "Holiday", color: "#64748b" },
    { value: "W", label: "Weekly Off", color: "#94a3b8" },
];
export const attendanceMeta = (code) => ATTENDANCE_STATUS.find((s) => s.value === code) || { value: code, label: code || "-", color: "#cbd5e1" };

// Recommended = approved by the reporting manager, waiting for HR (setting "Reporting Manager + HR")
export const LEAVE_STATUS = ["Pending", "Recommended", "Approved", "Rejected", "Cancelled"];
export const LOAN_TYPES = ["Loan", "Advance Salary"];
export const LOAN_STATUS = ["Active", "Completed", "Cancelled"];
export const INCENTIVE_SOURCES = ["Sales", "Workshop", "Manual"];
export const INCENTIVE_STATUS = ["Pending", "Approved", "Rejected", "Paid"];

// Payroll run life cycle: Draft -> Submitted -> Approved -> Posted (journal entry) -> Paid
export const PAYROLL_STATUS = ["Draft", "Submitted", "Approved", "Posted", "Paid"];

export const COMPONENT_TYPES = ["Earning", "Deduction"];
export const CALC_TYPES = ["Fixed", "% of Basic"];
export const YES_NO = [{ value: true, label: "Yes" }, { value: false, label: "No" }];

export const STATUS_COLORS = {
    Active: "success", Probation: "info", "On Notice": "warning", Resigned: "secondary", Terminated: "danger", Retired: "dark",
    Pending: "warning", Recommended: "info", Approved: "success", Rejected: "danger", Cancelled: "secondary", Completed: "secondary", Paid: "primary",
    Draft: "secondary", Submitted: "info", Posted: "primary",
};

export const MONTHS = [
    { label: "January", value: 1 }, { label: "February", value: 2 }, { label: "March", value: 3 },
    { label: "April", value: 4 }, { label: "May", value: 5 }, { label: "June", value: 6 },
    { label: "July", value: 7 }, { label: "August", value: 8 }, { label: "September", value: 9 },
    { label: "October", value: 10 }, { label: "November", value: 11 }, { label: "December", value: 12 },
];
export const monthName = (m) => MONTHS.find((x) => x.value === Number(m))?.label || "";

export const YEARS = Array.from({ length: 6 }, (_, i) => {
    const year = new Date().getFullYear() + 1 - i;
    return { label: String(year), value: year };
});

export const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const pad2 = (n) => String(n).padStart(2, "0");
export const toIsoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const todayIso = () => toIsoDate(new Date());

// Default In / Out for an employee: workshop shift times (shiftStart / shiftEnd from the API),
// else "(HH:mm - HH:mm)" in the shift text, else 09:00 - 18:00
export const shiftTimesOf = (emp = {}) => {
    if (emp.shiftStart && emp.shiftEnd) return [emp.shiftStart, emp.shiftEnd];
    const m = String(emp.shiftName || "").match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
    return m ? [m[1].padStart(5, "0"), m[2].padStart(5, "0")] : ["09:00", "18:00"];
};

// Early In: in earlyInMinutes or more before shift start. Early Out: out more than earlyOutMinutes before shift end
// (same rule as HrCore.EarlyFlags in the API). rules = timeRules from the daily attendance API.
export const earlyFlags = (emp, inTime, outTime, rules) => {
    const mins = (t) => { const [h, m] = String(t || "").split(":").map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null; };
    const [s, e] = shiftTimesOf(emp).map(mins);
    const end = e <= s ? e + 1440 : e;   // overnight shift
    const inM = mins(inTime);
    let outM = mins(outTime);
    if (outM !== null && outM < s) outM += 1440;
    return {
        earlyIn: !!rules?.earlyInEnabled && inM !== null && s - inM >= Math.max(1, rules.earlyInMinutes || 0),
        earlyOut: !!rules?.earlyOutEnabled && outM !== null && end - outM > Math.max(0, rules.earlyOutMinutes || 0),
    };
};

// In later than shift start + grace minutes (same rule as HrCore.ApplyGrace: Present is saved as Late)
export const isLateIn = (emp, inTime, rules) => {
    const [h, m] = String(inTime || "").split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return false;
    const [sh, sm] = shiftTimesOf(emp)[0].split(":").map(Number);
    const late = (((h * 60 + m) - (sh * 60 + sm)) % 1440 + 1440) % 1440;
    return late > Math.max(0, rules?.graceMinutes ?? 15) && late < 720;
};

// "HH:mm" + minutes (wraps at midnight)
export const addMinutes = (hhmm, minutes) => {
    const [h, m] = hhmm.split(":").map(Number);
    const t = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
    return `${pad2(Math.floor(t / 60))}:${pad2(t % 60)}`;
};

